import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import {
    computeWillBeUnmanagedOnDate,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';
import {
    useGetBenefitCheckConfigQuery,
    useGetMemberDetailsQuery,
} from 'store/api/scopeAssessorApi';

export const getMemberIdentityFromSource = (source) => ({
    memberId: source.memberId ?? source.memberDetails?.memberId,
    firstName: source.patientFirstName ?? source.firstName ?? source.memberDetails?.firstName,
    lastName: source.patientLastName ?? source.lastName ?? source.memberDetails?.lastName,
    dateOfBirth:
        source.patientDateOfBirth ??
        source.patientDob ??
        source.dateOfBirth ??
        source.memberDetails?.dateOfBirth,
});

/**
 * @param {object|undefined|null} source Referral, opportunity, or treatment payload with payer/segment/member ids.
 * @returns {typeof skipToken | { payerId: string, segmentId: string, memberId: string, firstName: string, lastName: string, dateOfBirth: string }}
 */
export const getScopeAssessorMemberDetailsQueryArgs = (source) => {
    if (!source?.payerId || !source?.segmentId) {
        return skipToken;
    }
    const { memberId, firstName, lastName, dateOfBirth } = getMemberIdentityFromSource(source);
    if (!memberId || !firstName || !lastName || !dateOfBirth) {
        return skipToken;
    }
    return {
        payerId: source.payerId,
        segmentId: source.segmentId,
        memberId,
        firstName,
        lastName,
        dateOfBirth,
    };
};

/**
 * Loads scope-assessor member details (and optional delayed-unmanagement date) for referral/opportunity UIs.
 *
 * @param {object|undefined|null} source Referral, opportunity, or treatment payload.
 * @param {{ computeWillBeUnmanagedOn?: boolean }} [options]
 */
export const useScopeAssessorMemberDetails = (
    source,
    { computeWillBeUnmanagedOn = false } = {},
) => {
    const { agoraEnhancedEligibility } = useFlags();
    const memberQueryArgs = useMemo(() => getScopeAssessorMemberDetailsQueryArgs(source), [source]);

    const {
        data: memberDetails,
        isFetching,
        isError,
        refetch,
    } = useGetMemberDetailsQuery(memberQueryArgs);

    const shouldFetchBenefitCheckConfig =
        computeWillBeUnmanagedOn &&
        agoraEnhancedEligibility &&
        memberQueryArgs !== skipToken &&
        memberDetails?.enrollmentStatus === PLATFORM_ELIGIBILITY_PENDING;

    const { data: delayedUnmanagementDays } = useGetBenefitCheckConfigQuery(
        shouldFetchBenefitCheckConfig
            ? {
                  payerId: memberQueryArgs.payerId,
                  segmentId: memberQueryArgs.segmentId,
              }
            : skipToken,
    );

    const willBeUnmanagedOn = useMemo(() => {
        if (!shouldFetchBenefitCheckConfig) {
            return memberDetails?.willBeUnmanagedOn;
        }
        return (
            computeWillBeUnmanagedOnDate(memberDetails?.pendingAt, delayedUnmanagementDays) ??
            memberDetails?.willBeUnmanagedOn ??
            undefined
        );
    }, [
        shouldFetchBenefitCheckConfig,
        delayedUnmanagementDays,
        memberDetails?.pendingAt,
        memberDetails?.willBeUnmanagedOn,
    ]);

    return {
        memberDetails,
        isFetching,
        isError,
        refetch,
        willBeUnmanagedOn,
    };
};
